// ============================================
// ADMIN: Messages - Farm-Booking
// Gestion des messages de contact + IMAP
// AVEC récupération emails + réponse directe
// ============================================

import { useState, useEffect } from 'react'

function AdminMessages({ onUpdate }) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showAll, setShowAll] = useState(false)
  
  // État pour la réponse
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [sending, setSending] = useState(false)
  const [replyStatus, setReplyStatus] = useState({ type: '', message: '' })
  
  // État IMAP
  const [fetchingMails, setFetchingMails] = useState(false)
  const [imapStatus, setImapStatus] = useState({ type: '', message: '' })
  
  // État archives
  const [showArchived, setShowArchived] = useState(false)
  
  // État nouveau message
  const [showNewMessage, setShowNewMessage] = useState(false)
  const [newMail, setNewMail] = useState({ to: '', subject: '', message: '' })
  const [sendingNew, setSendingNew] = useState(false)
  const [newMailStatus, setNewMailStatus] = useState({ type: '', message: '' })

  useEffect(() => {
    loadMessages()
  }, [showAll, showArchived])

  const loadMessages = async () => {
    try {
      let url = '/api/messages?'
      if (showAll) url += 'all=true&'
      if (showArchived) url += 'archived=true'
      const response = await fetch(url)
      const data = await response.json()
      setMessages(data)
    } catch (error) {
      console.error('Erreur:', error)
    }
    setLoading(false)
  }

  // 📬 Récupérer les nouveaux emails IMAP
  const handleFetchMails = async () => {
    setFetchingMails(true)
    setImapStatus({ type: '', message: '' })
    
    try {
      const response = await fetch('/api/imap/fetch')
      const data = await response.json()
      
      if (data.success) {
        setImapStatus({ 
          type: 'success', 
          message: `✅ ${data.data.fetched} email(s) récupéré(s)` 
        })
        // Recharger la liste
        loadMessages()
        onUpdate?.()
      } else {
        setImapStatus({ 
          type: 'error', 
          message: `❌ ${data.error}` 
        })
      }
    } catch (error) {
      console.error('Erreur fetch IMAP:', error)
      setImapStatus({ 
        type: 'error', 
        message: '❌ Erreur de connexion au serveur' 
      })
    }
    
    setFetchingMails(false)
    
    // Effacer le message après 5 secondes
    setTimeout(() => setImapStatus({ type: '', message: '' }), 5000)
  }

  const handleMarkRead = async (id) => {
    try {
      await fetch(`/api/messages/${id}/read`, { method: 'PUT' })
      loadMessages()
      onUpdate?.()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  const handleArchive = async (id) => {
    try {
      await fetch(`/api/messages/${id}/archive`, { method: 'PUT' })
      loadMessages()
      onUpdate?.()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  const handleUnarchive = async (id) => {
    try {
      await fetch(`/api/messages/${id}/unarchive`, { method: 'PUT' })
      loadMessages()
      onUpdate?.()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce message ?')) return
    try {
      await fetch(`/api/messages/${id}`, { method: 'DELETE' })
      setSelectedMessage(null)
      loadMessages()
      onUpdate?.()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  // Ouvrir le formulaire de réponse
  const openReplyForm = () => {
    setShowReplyForm(true)
    setReplyStatus({ type: '', message: '' })
    const prenom = selectedMessage.nom.split(' ')[0]
    setReplyContent(`Bonjour ${prenom},\n\nMerci pour votre message.\n\nCordialement,\nLa Ferme du Petit Essart`)
  }

  // Vérifier si adresse interne (pas de réponse possible)
  const isInternalEmail = (email) => {
    if (!email) return false
    const internal = ['contact@lafermedupetitessart.fr']
    return internal.includes(email.toLowerCase())
  }

  // Ouvrir modal nouveau message
  const openNewMessage = () => {
    setShowNewMessage(true)
    setNewMail({ to: '', subject: '', message: 'Bonjour,\n\n\n\nCordialement,\nLa Ferme du Petit Essart' })
    setNewMailStatus({ type: '', message: '' })
  }

  // Fermer modal nouveau message
  const closeNewMessage = () => {
    setShowNewMessage(false)
    setNewMail({ to: '', subject: '', message: '' })
    setNewMailStatus({ type: '', message: '' })
  }

  // Envoyer nouveau message
  const handleSendNewMail = async () => {
    if (!newMail.to.trim()) {
      setNewMailStatus({ type: 'error', message: 'Veuillez saisir une adresse email' })
      return
    }
    if (!newMail.message.trim()) {
      setNewMailStatus({ type: 'error', message: 'Veuillez écrire un message' })
      return
    }
    
    // Vérifier format email basique
    if (!newMail.to.includes('@')) {
      setNewMailStatus({ type: 'error', message: 'Adresse email invalide' })
      return
    }

    setSendingNew(true)
    setNewMailStatus({ type: '', message: '' })

    try {
      const response = await fetch('/api/messages/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: newMail.to.trim(),
          subject: newMail.subject.trim() || 'Message de La Ferme du Petit Essart',
          message: newMail.message
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setNewMailStatus({ type: 'success', message: '✅ Message envoyé avec succès !' })
        setTimeout(() => {
          closeNewMessage()
        }, 2000)
      } else {
        setNewMailStatus({ type: 'error', message: data.error || 'Erreur lors de l\'envoi' })
      }
    } catch (error) {
      console.error('Erreur envoi:', error)
      setNewMailStatus({ type: 'error', message: 'Erreur de connexion' })
    }

    setSendingNew(false)
  }

  // Fermer le formulaire de réponse
  const closeReplyForm = () => {
    setShowReplyForm(false)
    setReplyContent('')
    setReplyStatus({ type: '', message: '' })
  }

  // Envoyer la réponse
  const handleSendReply = async () => {
    if (!replyContent.trim()) {
      setReplyStatus({ type: 'error', message: 'Veuillez écrire un message' })
      return
    }

    if (!selectedMessage.email) {
      setReplyStatus({ type: 'error', message: 'Pas d\'adresse email pour ce contact' })
      return
    }

    setSending(true)
    setReplyStatus({ type: '', message: '' })

    try {
      const response = await fetch(`/api/messages/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: selectedMessage.email,
          subject: `Re: ${selectedMessage.sujet || 'Votre message'}`,
          message: replyContent,
          originalMessage: selectedMessage.message
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setReplyStatus({ type: 'success', message: '✅ Réponse envoyée avec succès !' })
        setTimeout(() => {
          closeReplyForm()
          loadMessages()
        }, 2000)
      } else {
        setReplyStatus({ type: 'error', message: data.error || 'Erreur lors de l\'envoi' })
      }
    } catch (error) {
      console.error('Erreur envoi réponse:', error)
      setReplyStatus({ type: 'error', message: 'Erreur de connexion' })
    }

    setSending(false)
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) return <div className="loading">Chargement...</div>

  return (
    <div className="admin-messages">
      <div className="admin-header">
        <h1>📬 Messages & Emails</h1>
        <p>{messages.length} message(s) {showAll ? '' : 'non lu(s)'}</p>
      </div>

      {/* Barre d'actions IMAP */}
      <div className="imap-toolbar">
        <button 
          className="btn-new-message"
          onClick={openNewMessage}
        >
          ✏️ Nouveau message
        </button>
        
        <button 
          className={`btn-fetch ${fetchingMails ? 'fetching' : ''}`}
          onClick={handleFetchMails}
          disabled={fetchingMails}
        >
          {fetchingMails ? '⏳ Récupération...' : '🔄 Récupérer les emails'}
        </button>
        
        {imapStatus.message && (
          <span className={`imap-status ${imapStatus.type}`}>
            {imapStatus.message}
          </span>
        )}
        
        <label className="show-all-toggle">
          <input 
            type="checkbox" 
            checked={showAll} 
            onChange={(e) => setShowAll(e.target.checked)} 
          />
          Tous les messages
        </label>
        
        <label className="show-all-toggle archives-toggle">
          <input 
            type="checkbox" 
            checked={showArchived} 
            onChange={(e) => setShowArchived(e.target.checked)} 
          />
          📥 Archives
        </label>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>📥 Boîte de réception</h3>
        </div>

        {messages.length === 0 ? (
          <p className="empty-state">
            🌾 Aucun message {showAll ? '' : 'non lu'}
          </p>
        ) : (
          <div className="messages-list">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`message-item ${!msg.lu ? 'unread' : ''} ${msg.replied_at ? 'replied' : ''}`}
                onClick={() => {
                  setSelectedMessage(msg)
                  setShowReplyForm(false)
                  if (!msg.lu) handleMarkRead(msg.id)
                }}
              >
                <div className="message-icon">
                  {msg.replied_at ? '✅' : (msg.lu ? '📭' : '📬')}
                </div>
                <div className="message-source">
                  {msg.source === 'imap' ? '📧' : '📝'}
                </div>
                <div className="message-info">
                  <div className="message-from">{msg.nom}</div>
                  <div className="message-subject">{msg.sujet || '(sans sujet)'}</div>
                </div>
                <div className="message-date">{formatDate(msg.received_at || msg.created_at)}</div>
                <div className="message-actions" onClick={(e) => e.stopPropagation()}>
                  {msg.archived ? (
                    <button 
                      className="btn-icon"
                      onClick={() => handleUnarchive(msg.id)}
                      title="Désarchiver"
                    >
                      📤
                    </button>
                  ) : (
                    <button 
                      className="btn-icon"
                      onClick={() => handleArchive(msg.id)}
                      title="Archiver"
                    >
                      📥
                    </button>
                  )}
                  <button 
                    className="btn-icon danger"
                    onClick={() => handleDelete(msg.id)}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal lecture message */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedMessage.sujet || '(sans sujet)'}</h3>
              <button className="btn-close" onClick={() => setSelectedMessage(null)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Infos expéditeur */}
              <div className="sender-info">
                <p><strong>De :</strong> {selectedMessage.nom} {selectedMessage.email && `<${selectedMessage.email}>`}</p>
                {selectedMessage.telephone && (
                  <p><strong>Tél :</strong> {selectedMessage.telephone}</p>
                )}
                <p><strong>Date :</strong> {formatDate(selectedMessage.received_at || selectedMessage.created_at)}</p>
                <p><strong>Source :</strong> {selectedMessage.source === 'imap' ? '📧 Email reçu' : '📝 Formulaire contact'}</p>
                {selectedMessage.replied_at && (
                  <p className="replied-info">
                    ✅ Répondu le {formatDate(selectedMessage.replied_at)}
                  </p>
                )}
              </div>
              
              {/* Message original */}
              <div className="message-content">
                <h4>Message :</h4>
                <div className="message-text">
                  {selectedMessage.message}
                </div>
              </div>

              {/* Formulaire de réponse */}
              {showReplyForm ? (
                <div className="reply-section">
                  <h4>📝 Votre réponse :</h4>
                  
                  <textarea
                    className="reply-textarea"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows="10"
                    placeholder="Écrivez votre réponse..."
                    disabled={sending}
                  />
                  
                  <div className="reply-info">
                    <small>
                      La réponse sera envoyée à : <strong>{selectedMessage.email}</strong>
                    </small>
                  </div>

                  {replyStatus.message && (
                    <div className={`alert ${replyStatus.type}`}>
                      {replyStatus.message}
                    </div>
                  )}

                  <div className="reply-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleSendReply}
                      disabled={sending}
                    >
                      {sending ? '⏳ Envoi en cours...' : '📧 Envoyer la réponse'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={closeReplyForm}
                      disabled={sending}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                /* Boutons d'action */
                <div className="action-buttons">
                  {/* STAMP RÉPONSE FAITE */}
                  {selectedMessage.replied_at && (
                    <div className="stamp-replied">
                      ✅ RÉPONSE ENVOYÉE
                      <span className="stamp-date">le {formatDate(selectedMessage.replied_at)}</span>
                    </div>
                  )}
                  
                  {/* Bouton Répondre */}
                  {selectedMessage.email && !isInternalEmail(selectedMessage.email) && (
                    <button 
                      className="btn btn-primary"
                      onClick={openReplyForm}
                    >
                      ✉️ {selectedMessage.replied_at ? 'Répondre à nouveau' : 'Répondre'}
                    </button>
                  )}
                  
                  {/* Adresse interne - pas de réponse */}
                  {selectedMessage.email && isInternalEmail(selectedMessage.email) && (
                    <div className="internal-email-warning">
                      🚫 Adresse interne - Réponse impossible
                    </div>
                  )}
                  
                  {selectedMessage.telephone && (
                    <a 
                      href={`tel:${selectedMessage.telephone}`}
                      className="btn btn-secondary"
                    >
                      📞 Appeler
                    </a>
                  )}
                  
                  {!selectedMessage.email && (
                    <p className="no-email">
                      ⚠️ Pas d'adresse email - Réponse par téléphone uniquement
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal nouveau message */}
      {showNewMessage && (
        <div className="modal-overlay" onClick={closeNewMessage}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header new-message-header">
              <h3>✏️ Nouveau message</h3>
              <button className="btn-close" onClick={closeNewMessage}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="new-message-form">
                <div className="form-group">
                  <label>À :</label>
                  <input
                    type="email"
                    className="form-input"
                    value={newMail.to}
                    onChange={(e) => setNewMail({ ...newMail, to: e.target.value })}
                    placeholder="destinataire@email.com"
                    disabled={sendingNew}
                  />
                </div>
                
                <div className="form-group">
                  <label>Sujet :</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newMail.subject}
                    onChange={(e) => setNewMail({ ...newMail, subject: e.target.value })}
                    placeholder="Objet du message"
                    disabled={sendingNew}
                  />
                </div>
                
                <div className="form-group">
                  <label>Message :</label>
                  <textarea
                    className="reply-textarea"
                    value={newMail.message}
                    onChange={(e) => setNewMail({ ...newMail, message: e.target.value })}
                    rows="12"
                    placeholder="Votre message..."
                    disabled={sendingNew}
                  />
                </div>
                
                <div className="sender-note">
                  📧 Envoyé depuis : <strong>contact@lafermedupetitessart.fr</strong>
                </div>

                {newMailStatus.message && (
                  <div className={`alert ${newMailStatus.type}`}>
                    {newMailStatus.message}
                  </div>
                )}

                <div className="reply-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleSendNewMail}
                    disabled={sendingNew}
                  >
                    {sendingNew ? '⏳ Envoi en cours...' : '📧 Envoyer'}
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={closeNewMessage}
                    disabled={sendingNew}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .imap-toolbar {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 20px;
          padding: 15px;
          background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
          border-radius: 12px;
          flex-wrap: wrap;
        }
        
        .btn-fetch {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: white;
          color: #2e7d32;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        .btn-fetch:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        
        .btn-fetch:disabled {
          opacity: 0.7;
          cursor: wait;
        }
        
        .btn-fetch.fetching {
          animation: pulse 1s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .imap-status {
          padding: 8px 15px;
          border-radius: 6px;
          font-weight: 500;
        }
        
        .imap-status.success {
          background: rgba(255,255,255,0.9);
          color: #2e7d32;
        }
        
        .imap-status.error {
          background: #ffebee;
          color: #c62828;
        }
        
        .show-all-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          cursor: pointer;
          margin-left: auto;
        }
        
        .show-all-toggle input {
          width: 18px;
          height: 18px;
        }
        
        .archives-toggle {
          background: rgba(255,255,255,0.2);
          padding: 6px 12px;
          border-radius: 6px;
        }
        
        .messages-list {
          display: flex;
          flex-direction: column;
        }
        
        .message-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 15px 20px;
          border-bottom: 1px solid #e0e0e0;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .message-item:hover {
          background: #f5f5f5;
        }
        
        .message-item.unread {
          background: #fff8e1;
          font-weight: 600;
        }
        
        .message-item.replied {
          background: #e8f5e9;
        }
        
        .message-icon {
          font-size: 1.3rem;
        }
        
        .message-source {
          font-size: 1rem;
          opacity: 0.7;
        }
        
        .message-info {
          flex: 1;
        }
        
        .message-from {
          font-size: 1rem;
          color: #333;
        }
        
        .message-subject {
          font-size: 0.9rem;
          color: #666;
        }
        
        .message-date {
          font-size: 0.85rem;
          color: #888;
          white-space: nowrap;
        }
        
        .message-actions {
          display: flex;
          gap: 8px;
        }
        
        .btn-icon {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 5px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        
        .btn-icon:hover {
          background: #e0e0e0;
        }
        
        .btn-icon.danger:hover {
          background: #ffebee;
        }
        
        .empty-state {
          padding: 40px;
          text-align: center;
          color: #666;
        }
        
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
          background: #f5f5f5;
        }
        
        .modal-header h3 {
          margin: 0;
          color: #333;
        }
        
        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
        }
        
        .modal-body {
          padding: 20px;
        }
        
        .sender-info {
          background: #fafafa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .sender-info p {
          margin: 5px 0;
        }
        
        .replied-info {
          color: #2e7d32;
          font-weight: 600;
          margin-top: 10px !important;
        }
        
        .message-content h4 {
          margin-bottom: 10px;
          color: #333;
        }
        
        .message-text {
          white-space: pre-wrap;
          line-height: 1.7;
          padding: 15px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
        }
        
        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 25px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
          align-items: center;
        }
        
        .no-email {
          color: #c53030;
          font-style: italic;
        }
        
        .stamp-replied {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: bold;
          font-size: 1.1rem;
          box-shadow: 0 3px 10px rgba(46, 125, 50, 0.3);
          border: 2px solid #1b5e20;
        }
        
        .stamp-replied .stamp-date {
          font-size: 0.8rem;
          font-weight: normal;
          opacity: 0.9;
          margin-top: 4px;
        }
        
        .internal-email-warning {
          background: #fff3e0;
          color: #e65100;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: 600;
          border: 2px solid #ffb74d;
        }
        
        .btn-new-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #ff9800;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
        }
        
        .btn-new-message:hover {
          background: #f57c00;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
        }
        
        .new-message-header {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
        }
        
        .new-message-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .form-group label {
          font-weight: 600;
          color: #333;
        }
        
        .form-input {
          padding: 12px 15px;
          font-size: 1rem;
          border: 2px solid #ddd;
          border-radius: 8px;
          transition: border-color 0.2s;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #ff9800;
          box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.2);
        }
        
        .sender-note {
          background: #f5f5f5;
          padding: 10px 15px;
          border-radius: 6px;
          font-size: 0.9rem;
          color: #666;
        }
        
        .reply-section {
          margin-top: 25px;
          padding-top: 20px;
          border-top: 2px solid #4CAF50;
        }
        
        .reply-section h4 {
          color: #2e7d32;
          margin-bottom: 15px;
        }
        
        .reply-textarea {
          width: 100%;
          min-height: 150px;
          padding: 15px;
          font-size: 1rem;
          font-family: inherit;
          line-height: 1.6;
          border: 2px solid #4CAF50;
          border-radius: 8px;
          resize: vertical;
        }
        
        .reply-textarea:focus {
          outline: none;
          border-color: #2e7d32;
          box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
        }
        
        .reply-info {
          margin: 10px 0 15px;
          color: #666;
        }
        
        .reply-actions {
          display: flex;
          gap: 12px;
          margin-top: 15px;
        }
        
        .alert {
          padding: 12px 15px;
          border-radius: 8px;
          margin: 15px 0;
        }
        
        .alert.success {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }
        
        .alert.error {
          background: #ffebee;
          color: #c53030;
          border: 1px solid #ffcdd2;
        }
        
        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-primary {
          background: #4CAF50;
          color: white;
        }
        
        .btn-primary:hover {
          background: #43a047;
        }
        
        .btn-secondary {
          background: #e0e0e0;
          color: #333;
        }
        
        .btn-secondary:hover {
          background: #d0d0d0;
        }
        
        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .card-header {
          padding: 15px 20px;
          background: #fafafa;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .card-header h3 {
          margin: 0;
          color: #333;
        }
      `}</style>
    </div>
  )
}

export default AdminMessages